import React, { useCallback, useEffect, useState, SetStateAction, Dispatch } from 'react';
import { getMonetaryDisplayValue } from 'shared/utils';
import { FilteredLineItemsType } from './Invoice';
import { IAdjustment } from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/hooks/useMarkPetBenefitUsage';
import { Text, Tbody, Td, Th, Thead, Tr, VStack, Box } from '@televet/kibble-ui';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

interface IInvoiceLineItemTableProps {
  filteredLineItems: FilteredLineItemsType;
  isMobile: boolean;
  clinicPetId: string;
  setPatientDiscounts?: Dispatch<SetStateAction<Record<string, number>>>;
  adjustments: IAdjustment[];
  fromInvoicePreview?: boolean;
}

const InvoiceLineItemTable = ({
  filteredLineItems,
  isMobile,
  setPatientDiscounts,
  clinicPetId,
  adjustments,
  fromInvoicePreview,
}: IInvoiceLineItemTableProps): JSX.Element => {
  const { isServiceCodeSupported } = useIntegrationsProvider();
  const [lineItems, setLineItems] = useState<FilteredLineItemsType>(filteredLineItems);

  useEffect(() => {
    setLineItems(filteredLineItems);
  }, [filteredLineItems]);

  const getLineItemDiscount = useCallback(
    (lineItemId: string, benefitIdToExclude?: string): number => {
      return adjustments
        .filter((adjustment) => adjustment.lineItemIds.includes(lineItemId))
        .filter((adjustment) => adjustment.benefitId !== benefitIdToExclude)
        .reduce((prev, curr) => (prev += parseInt(curr.adjustedValue)), 0);
    },
    [adjustments],
  );

  const getLineItemAmountAfterConnectedBenefits = (lineItem: FilteredLineItemsType[0]): number => {
    // adjusted amount = original amount - connected benefits
    return lineItem.amount + lineItem.fee - lineItem.discount;
  };

  // recalculate patientDiscounts
  useEffect(() => {
    if (!setPatientDiscounts) return;

    setPatientDiscounts((currentPatientDiscounts) => {
      const newPatientDiscounts = { ...currentPatientDiscounts };
      newPatientDiscounts[clinicPetId] = adjustments
        .filter((adjustment) => adjustment.clinicPetId === clinicPetId)
        .reduce((prev, curr) => (prev += parseInt(curr.adjustedValue)), 0);
      return newPatientDiscounts;
    });
  }, [adjustments, clinicPetId, setPatientDiscounts]);

  // TODO: Description is not being displayed correctly in the invoice preview
  return (
    <>
      <Thead>
        <Tr>
          {isServiceCodeSupported && !isMobile && <Th>code</Th>}
          <Th>description</Th>
          <Th>QTY</Th>
          <Th>Provider</Th>
          <Th>total</Th>
        </Tr>
      </Thead>
      <Tbody>
        {lineItems.map((lineItem) => {
          return (
            <Tr key={lineItem.id} borderTop="1px">
              {isServiceCodeSupported && !isMobile && <Td>{lineItem.service?.code}</Td>}
              <Td>
                <Box>
                  {lineItem.description}
                  {isServiceCodeSupported && isMobile && (
                    <Text size="sm" alignSelf="flex-start">{`CODE: ${lineItem.service?.code ?? ''}`}</Text>
                  )}
                </Box>
              </Td>
              <Td>{lineItem.quantity}</Td>
              <Td>
                {lineItem.provider?.firstName} {lineItem.provider?.lastName}
              </Td>
              <Td alignItems="center">
                <>
                  <>
                    {!!lineItem.discount ? (
                      <>
                        <VStack>
                          <Text variant="danger" as="s" mr="-3px" overflowWrap="normal">
                            ${getMonetaryDisplayValue(lineItem.amount + lineItem.fee)}
                          </Text>
                          <Text mr="-4px" overflowWrap="normal">
                            ${getMonetaryDisplayValue(lineItem.amount + lineItem.fee - lineItem.discount)}
                          </Text>
                        </VStack>
                      </>
                    ) : (
                      <Text mx="2.5">
                        {getMonetaryDisplayValue(
                          fromInvoicePreview
                            ? getLineItemAmountAfterConnectedBenefits(lineItem)
                            : lineItem.amount + lineItem.fee - (getLineItemDiscount(lineItem.id) || 0),
                          true,
                        )}
                      </Text>
                    )}
                  </>
                </>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </>
  );
};

export default InvoiceLineItemTable;
